import { Calendar, Clock, MapPin, Share2, ExternalLink, Laptop, CalendarPlus2, ClockFading, CalendarMinus2 } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import redirectTo from '../../../utils/redirect';
import { ShareModal } from './ShareModal';
import { AddToCalendarModal } from './AddToCalendarModal';
import { slugify } from '../../../utils/slugify';
import { Event } from "../../../services/EventService";
import { CATEGORIES } from '../../../constants/categories';
import { MODALITIES } from '../../../constants/modalities';
import { getTimeZoneLabel, normalizeTimeZone } from '../../../constants/timezones';

interface Host {
  id: string;
  name: string;
  avatar_url: string;
  website?: string;
  user_id?: string;
  status?: 'pending' | 'accepted' | 'declined';
}

interface SideEvent {
  id: string;
  title: string;
  description: string;
  image_url?: string;
  start_time?: string;
  start_date?: string;
  location: string;
  hosts: Host[];
  category?: string;
  modality?: string;
}

interface EventDetailCardProps {
  event: {
    id: string;
    title?: string;
    description?: string;
    image_url?: string;
    website_link?: string;
    date?: string;
    start_time?: string;
    start_date?: string;
    end_date?: string;
    end_time?: string;
    location?: string;
    location_detail?: string;
    attendees?: number;
    hosts?: Host[];
    time_zone: string;
    category?: string;
    modality?: string;
    eventDetails?: string;
    sideEvents?: SideEvent[];
    icsFileUrl?: string;
    parent_event_id?: string;
    parent_event?: any;
  };
}

export const EventDetailCard = ({ event }: EventDetailCardProps) => {
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isCalendarModalOpen, setIsCalendarModalOpen] = useState(false);
  const [sideEvents, setSideEvents] = useState<SideEvent[]>([]);
  const [parentEvent, setParentEvent] = useState<any | null>(null);
  const [activeCalendarEvent, setActiveCalendarEvent] = useState<any | null>(null);
  const [isLoadingSideEvents, setIsLoadingSideEvents] = useState(false);
  const [isMainImageVisible, setIsMainImageVisible] = useState(true);
  const [isParentImageVisible, setIsParentImageVisible] = useState(true);
  const [hiddenSideEventImageIds, setHiddenSideEventImageIds] = useState<Set<string>>(new Set());
  const [hiddenHostAvatarIds, setHiddenHostAvatarIds] = useState<Set<string>>(new Set());
  const [hiddenSideHostAvatarIds, setHiddenSideHostAvatarIds] = useState<Set<string>>(new Set());
  
  // Fetch side events and parent event when component mounts
  useEffect(() => {
    // If this is a main event, fetch its side events
    if (event.id && !event.parent_event_id) {
      fetchSideEvents(event.id);
    } 
    // If this is a side event and we don't have parent event details yet, fetch them
    else if (event.parent_event_id && !event.parent_event) {
      fetchParentEvent(event.parent_event_id);
    } 
    // If we already have parent event details, use them
    else if (event.parent_event_id && event.parent_event) {
      setParentEvent(event.parent_event);
    }
  }, [event.id, event.parent_event_id, event.parent_event]);

  // Function to fetch side events
  const fetchSideEvents = async (eventId: string) => {
    try {
      setIsLoadingSideEvents(true);
      const { data, error } = await Event.getSideEvents(eventId);
      
      if (error) {
        console.error("Error fetching side events:", error);
      } else if (data) {
        // Transform the data to match your SideEvent interface
        const transformedEvents = data.map((sideEvent: any) => ({
          id: sideEvent.id,
          title: sideEvent.title || 'Untitled Event',
          description: sideEvent.short_description || sideEvent.description || '',
          image_url: sideEvent.image_url || undefined,
          start_time: sideEvent.start_time,
          start_date: sideEvent.start_date,
          location: sideEvent.location || 'TBA',
          hosts: sideEvent.hosts || [],
          category: sideEvent.category,
          modality: sideEvent.modality
        }));
        
        setSideEvents(transformedEvents);
      }
    } catch (e) {
      console.error("Exception fetching side events:", e);
    } finally {
      setIsLoadingSideEvents(false);
    }
  };

  // Function to fetch parent event
  const fetchParentEvent = async (parentId: string) => {
    try {
      const { data, error } = await Event.getById(parentId);
      
      if (error) {
        console.error("Error fetching parent event:", error);
      } else if (data) {
        setParentEvent(data);
      }
    } catch (e) {
      console.error("Exception fetching parent event:", e);
    }
  };

  // Format date and time
  const formatEventDate = (dateString?: string) => {
    if (!dateString) return 'TBA';
    const options: Intl.DateTimeFormatOptions = { 
      month: 'long', 
      day: 'numeric',
      year: 'numeric'
    };
    try {
      let date: Date;
      // Parse YYYY-MM-DD as a local date to avoid timezone shifting to previous day
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        const [year, month, day] = dateString.split('-').map(Number);
        date = new Date(year, month - 1, day);
      } else {
        const parsed = Date.parse(dateString);
        if (!Number.isNaN(parsed)) {
          date = new Date(parsed);
        } else {
          return dateString;
        }
      }
      return date.toLocaleDateString('en-US', options);
    } catch {
      return dateString;
    }
  };

  const formatEventTime = (timeString?: string) => {
    if (!timeString) return 'TBA';
    
    try {
      const [hours, minutes] = timeString.split(':');
      const date = new Date();
      date.setHours(parseInt(hours, 10), parseInt(minutes, 10));
      return date.toLocaleTimeString('en-US', { 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: true 
      });
    } catch (e) {
      return timeString;
    }
  };
  
  const handleHostClick = (host?: Host) => {
    if (!host?.website) return;
    window.open(host.website, '_blank');
  };

  const handleAddToCalendar = (eventData: any) => {
    setActiveCalendarEvent(eventData);
    setIsCalendarModalOpen(true);
  };

  // Filtra los anfitriones para mostrar solo los aceptados
  const acceptedHosts = event.hosts?.filter(host => host.status === 'accepted');


  // Get category and modality styling
  const categoryStyle = event.category ? CATEGORIES.find(cat => cat.name === event.category) : null;
  const modalityStyle = event.modality ? MODALITIES.find(mod => mod.value === event.modality) : null;

  // Timezone handling
  const eventTZValue = normalizeTimeZone(event.time_zone);
  const eventTZLabel = eventTZValue;

  return (
    <div className="bg-white rounded-lg shadow-lg p-4 sm:p-6">
      {/* Header Section */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 sm:gap-0 mb-6">
        <button onClick={() => redirectTo('/')} className="flex items-center gap-2 text-primary hover:underline text-sm sm:text-base">
          ← Volver
        </button>
        <Button 
          className="flex items-center gap-2 w-full sm:w-auto justify-center" 
          variant="outline" 
          style={{borderColor: '#0095FF', color: '#0095FF'}}
          onClick={() => setIsShareModalOpen(true)}
          size="sm"
        >
          <Share2 className="w-4 h-4" /> Compartir
        </Button>
      </div>

      {/* Parent Event Section - Display if this is a side event */}
      {event.parent_event_id && parentEvent && (
        <div className="mb-6 bg-blue-50 rounded-lg p-3 sm:p-4 border border-blue-100">
          <div className="flex items-center gap-2 mb-2">
            <ExternalLink className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600" />
            <h3 className="text-base sm:text-lg font-medium text-blue-800">Este es un evento secundario</h3>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
            {parentEvent.image_url && isParentImageVisible && (
              <img 
                src={parentEvent.image_url} 
                alt={parentEvent.title} 
                className="w-full sm:w-24 h-32 sm:h-24 rounded-lg object-cover" 
                onError={() => setIsParentImageVisible(false)}
              />
            )}
            
            <div className="flex-1">
              <h4 className="font-semibold mb-1 text-sm sm:text-base">Parte de: {parentEvent.title}</h4>
              <p className="text-xs sm:text-sm text-gray-600 mb-2 line-clamp-2">
                {parentEvent.short_description || parentEvent.description}
              </p>
              
              <Link 
                to={`/event/${slugify(parentEvent.title)}`}
                className="inline-flex items-center gap-2 bg-white text-primary font-medium py-1 px-3 rounded-md border border-primary/30 hover:bg-primary/5 transition-colors text-xs sm:text-sm"
              >
                <span>Ver evento principal</span>
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="flex flex-col lg:flex-row gap-4 lg:gap-6 mb-6">
        {event.image_url && isMainImageVisible && (
          <div className="w-full lg:w-auto lg:flex-shrink-0">
            <img 
              src={event.image_url} 
              alt={event.title || 'Event'} 
              className="w-full  h-48 object-cover rounded-lg"
              onError={() => setIsMainImageVisible(false)}
            />
          </div>
        )}
        
        <div className="flex-1">
          {/* Category and Modality Badges */}
          <div className="flex flex-wrap items-center gap-2 mb-4">
            {event.category && categoryStyle && (
              <span 
                className="text-xs px-3 sm:px-4 py-1.5 sm:py-2 rounded-md font-medium"
                style={{ 
                  backgroundColor: categoryStyle.color, 
                  color: categoryStyle.textColor 
                }}
              >
                {event.category}
              </span>
            )}
            {event.modality && modalityStyle && (
              <span 
                className="text-xs font-medium px-3 sm:px-4 py-1.5 sm:py-2 rounded-md"
                style={{ 
                  backgroundColor: modalityStyle.color, 
                  color: modalityStyle.textColor 
                }}
              >
                {modalityStyle.name}
              </span>
            )}
            
            {/* Side Event Badge */}
            {event.parent_event_id && (
              <span className="text-xs px-2 py-1 rounded-md font-medium bg-blue-100 text-blue-600">
                Evento secundario
              </span>
            )}
          </div>

          <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold mb-2 leading-tight">{event.title || 'Próximo evento'}</h1>
          
          {/* Hosts */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 text-xs sm:text-sm text-muted-foreground mb-4">
            <div className="flex items-center -space-x-2">
              {acceptedHosts?.map((host, index) => {
                const showImg = Boolean(host.avatar_url) && !hiddenHostAvatarIds.has(host.id);
                return (
                  <div
                    key={host.id}
                    className="w-5 h-5 sm:w-6 sm:h-6 rounded-full border-2 border-white cursor-pointer hover:scale-110 transition-transform flex items-center justify-center overflow-hidden bg-primary/10"
                    style={{ zIndex: (acceptedHosts?.length ?? 0) - index }}
                    title={host.name}
                    onClick={() => handleHostClick(host)}
                  >
                    {showImg ? (
                      <img
                        src={host.avatar_url}
                        alt={host.name}
                        className="w-full h-full object-cover"
                        onError={() => setHiddenHostAvatarIds(prev => { const next = new Set(prev); next.add(host.id); return next; })}
                      />
                    ) : (
                      <span className="text-[10px] sm:text-xs font-medium text-primary">
                        {host.name?.charAt(0) || '?'}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
            
            <div className="flex flex-wrap items-center gap-1">
              <span>Organizado por</span>
              {acceptedHosts?.map((host, index) => (
                <span key={host.id} className="inline-flex items-center">
                  <span 
                    className="font-medium cursor-pointer hover:underline text-primary"
                    onClick={() => handleHostClick(host)}
                  >
                    {host.name}
                  </span>
                  {acceptedHosts && acceptedHosts.length > 2 && index < acceptedHosts.length - 2 && <span className="text-muted-foreground">,</span>}
                  {acceptedHosts && acceptedHosts.length > 1 && index === acceptedHosts.length - 2 && <span className="text-muted-foreground">&nbsp;and&nbsp;</span>}
                </span>
              ))}
            </div>
          </div>

          {/* Website */}
          {event.website_link && (
            <div className="flex items-center gap-2 mb-4">
              <ExternalLink className="w-4 h-4" />
              <a href={event.website_link} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-xs sm:text-sm break-all">
                {event.website_link}
              </a>
            </div>
          )}

          {/* Time Zone (single display) */}
          <div className="flex items-center gap-1 text-xs sm:text-sm text-muted-foreground mb-1">
            <Clock className="w-3 h-3 sm:w-4 sm:h-4" />
            <span>{eventTZLabel}</span>
          </div>
          {/* Date and Time */}
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 text-xs sm:text-sm text-muted-foreground mb-4">
            <div className="flex items-center gap-1">
              <Calendar className="w-3 h-3 sm:w-4 sm:h-4" /> 
            <span>desde {event.start_date ? formatEventDate(event.start_date) : 'Fecha a confirmar'}</span>
            </div>
            <div className="flex items-center gap-1">
              <Clock className="w-3 h-3 sm:w-4 sm:h-4" /> 
              <span>{event.start_time ? formatEventTime(event.start_time) : 'Hora a confirmar'}</span>
            </div>
          </div>
          {/* DDate End and Time */}
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 text-xs sm:text-sm text-muted-foreground mb-4">
            <div className="flex items-center gap-1">
              <CalendarMinus2 className="w-3 h-3 sm:w-4 sm:h-4" /> 
              <span>hasta {event.end_date ? formatEventDate(event.end_date) : 'Fecha a confirmar'} </span>
            </div>
            <div className="flex items-center gap-1">
              <ClockFading className="w-3 h-3 sm:w-4 sm:h-4" /> 
              <span>{event.start_time ? formatEventTime(event.start_time) : 'Hora a confirmar'}</span>
            </div>
          </div>
          {/* DAte End and Time*/}
          

          {/* Location */}
          {event.location && (
            <div className="flex items-start gap-2 text-xs sm:text-sm text-muted-foreground mb-4">
              <MapPin className="w-3 h-3 sm:w-4 sm:h-4 mt-0.5" />
              <div className="flex-1 min-w-0">
                <a 
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${event.location} ${event.location_detail || ''}`.trim())}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-blue-600"
                >
                  <div className="font-medium text-black hover:underline whitespace-pre-wrap break-words break-all">
                    {event.location}
                  </div>
                  {event.location_detail && (
                    <div className="hover:underline text-gray-600 whitespace-pre-wrap break-words break-all">
                      {event.location_detail}
                    </div>
                  )}
                </a>
              </div>
            </div>
          )}

          {/* Add to Calendar Button */}
          <Button 
            className="w-full border-2 border-primary/30 text-primary bg-primary/5 hover:bg-primary/10"
            onClick={() => setIsCalendarModalOpen(true)}
            size="sm"
          >
            <CalendarPlus2 className="w-4 h-4 mr-2" /> Agregar al calendario
          </Button>
        </div>
      </div>

      {/* Event Details Section */}
      <div className="border-t pt-4 sm:pt-6 mb-4 sm:mb-6">
        <h2 className="text-lg sm:text-xl font-bold mb-3 sm:mb-4">Detalles del evento</h2>
        <div className="text-sm sm:text-base text-gray-700 leading-relaxed whitespace-pre-line">
          {event.description || 'No hay detalles disponibles para este evento.'}
        </div>
      </div>

      {/* Side Events Section - Only show if this is a main event */}
      {!event.parent_event_id && (
        <div className="pt-4 sm:pt-6">
          {isLoadingSideEvents ? (
            <div className="flex justify-center items-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : sideEvents.length > 0 ? (
            <div>
              <h2 className="text-lg sm:text-xl font-bold mb-3 sm:mb-4">Eventos secundarios</h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                {sideEvents.map((sideEvent) => {
                  const sideEventCategory = sideEvent.category ? CATEGORIES.find(cat => cat.name === sideEvent.category) : null;
                  const sideEventModality = sideEvent.modality ? MODALITIES.find(mod => mod.value === sideEvent.modality) : null;
                  
                  return (
                    <div key={sideEvent.id} className="border rounded-lg p-3 sm:p-4 bg-gray-50">
                      <div className="flex flex-col sm:flex-row gap-3">
                        {sideEvent.image_url && !hiddenSideEventImageIds.has(sideEvent.id) && (
                          <img 
                            src={sideEvent.image_url} 
                            alt={sideEvent.title} 
                            className="w-full sm:w-48 h-32 sm:h-28 rounded-lg object-cover"
                            onError={() => {
                              setHiddenSideEventImageIds(prev => {
                                const next = new Set(prev);
                                next.add(sideEvent.id);
                                return next;
                              });
                            }}
                          />
                        )}
                        
                        <div className="w-full">
                          <div className="flex flex-wrap items-center gap-2 mb-2">
                            {sideEvent.category && sideEventCategory && (
                              <span 
                                className="inline-block px-2 sm:px-3 py-1 text-xs rounded font-medium"
                                style={{ 
                                  backgroundColor: sideEventCategory.color, 
                                  color: sideEventCategory.textColor 
                                }}
                              >
                                {sideEvent.category}
                              </span>
                            )}
                            {sideEvent.modality && sideEventModality && (
                              <span 
                                className="inline-block px-2 sm:px-3 py-1 text-xs rounded-full font-medium"
                                style={{ 
                                  backgroundColor: sideEventModality.color, 
                                  color: sideEventModality.textColor 
                                }}
                              >
                                {sideEventModality.name}
                              </span>
                            )}
                          </div>
                          <h3 className="text-base sm:text-lg font-bold mb-1">{sideEvent.title}</h3>
                          <p className="text-xs sm:text-sm text-gray-600 line-clamp-2">{sideEvent.description}</p>
                        </div>
                      </div>

                      {/* Hosts */}
                      <div className="flex items-center gap-2 text-xs text-gray-600 mb-3 mt-3">
                        <div className="flex items-center -space-x-1">
                          {sideEvent.hosts.map((host, index) => {
                            const showImg = Boolean(host.avatar_url) && !hiddenSideHostAvatarIds.has(host.id);
                            return (
                              <div
                                key={host.id}
                                className="w-4 h-4 rounded-full border border-white flex items-center justify-center overflow-hidden bg-primary/10"
                                style={{ zIndex: sideEvent.hosts.length - index }}
                                title={host.name}
                              >
                                {showImg ? (
                                  <img
                                    src={host.avatar_url}
                                    alt={host.name}
                                    className="w-full h-full object-cover"
                                    onError={() => setHiddenSideHostAvatarIds(prev => { const next = new Set(prev); next.add(host.id); return next; })}
                                  />
                                ) : (
                                  <span className="text-[10px] font-medium text-primary">
                                    {host.name?.charAt(0) || '?'}
                                  </span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                        <span>Hosted by</span>
                        <span className="text-primary font-medium">
                          {sideEvent.hosts.map(host => host.name).join(' and ')}
                        </span>
                      </div>

                      {/* Date, Time, Location */}
                      <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-xs text-gray-600 mb-4 sm:min-w-0">
                        <div className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" /> {formatEventDate(sideEvent.start_date)}
                        </div>
                        {sideEvent.start_time && (
                          <div className="flex items-center gap-1">
                            <Clock className="w-3 h-3" /> {formatEventTime(sideEvent.start_time)}
                          </div>
                        )}
                        {sideEvent.location && (
                          <div className="flex items-center gap-1 sm:flex-1 sm:min-w-0">
                            <MapPin className="w-3 h-3" /> 
                            <span className="truncate">{sideEvent.location}</span>
                          </div>
                        )}
                      </div>

                      {/* Buttons */}
                      <div className="space-y-2">
                        <Button 
                          className="w-full border border-primary/30 text-primary bg-primary/5 hover:bg-primary/10 text-xs sm:text-sm"
                          onClick={() => {
                            handleAddToCalendar({
                              title: sideEvent.title,
                              description: sideEvent.description,
                              startDate: sideEvent.start_date,
                              startTime: sideEvent.start_time,
                              endTime: calculateEndTime(sideEvent.start_time || "12:00"),
                              location: sideEvent.location || 'TBA',
                            });
                            setIsCalendarModalOpen(true);
                          }}
                          size="sm"
                        >
                          <CalendarPlus2 className="w-3 h-3 mr-1" /> Agregar al calendario
                        </Button>
                        <Link 
                          to={`/event/${slugify(sideEvent.title)}`}
                          className="block w-full text-center text-xs sm:text-sm font-medium text-gray-700 hover:underline"
                        >
                          Ver detalles
                        </Link>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <>
              
            </>
          )}
        </div>
      )}

      {/* Share Modal */}
      <ShareModal
        isOpen={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
        title={event.title || 'Evento'}
        url={`https://cap.com/event/${slugify(event.title || 'evento-sin-titulo')}`}
        description="Los eventos son mejores si los compartís con amigos."
      />

      {/* Calendar Modal */}
      {activeCalendarEvent ? (
        <AddToCalendarModal
          isOpen={isCalendarModalOpen}
          onClose={() => {
            setIsCalendarModalOpen(false);
            setActiveCalendarEvent(null);
          }}
          eventData={{
            id: event.id,
            title: activeCalendarEvent.title ?? 'Evento sin título',
            description: activeCalendarEvent.description ?? 'Sin descripción disponible',
            startDate: activeCalendarEvent.startDate ?? new Date().toISOString().split('T')[0],
            startTime: activeCalendarEvent.startTime ?? "12:00",
            endTime: activeCalendarEvent.endTime ?? "14:00",
            location: activeCalendarEvent.location ?? 'A confirmar',
          }}
        />
      ) : (
        <AddToCalendarModal
          isOpen={isCalendarModalOpen}
          onClose={() => setIsCalendarModalOpen(false)}
          eventData={{
            id: event.id,
            title: event.title ?? 'Evento sin título',
            description: event.eventDetails ?? event.description ?? 'Sin descripción disponible',
            startDate: event.start_date ?? "2024-03-26",
            startTime: event.start_time ?? "21:00",
            endTime: event.end_time ? calculateEndTime(event.end_time) : "02:00",
            location: event.location ? `${event.location}${event.location_detail ? `, ${event.location_detail}` : ''}` : 'A confirmar',
          }}
        />
      )}
    </div>
  );
};

// Helper function to calculate end time (2 hours after start time)
function calculateEndTime(startTime: string): string {
  try {
    const [hours, minutes] = startTime.split(':').map(Number);
    const date = new Date();
    date.setHours(hours, minutes);
    date.setHours(date.getHours() + 2); // Add 2 hours
    return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
  } catch (e) {
    return "00:00";
  }
}